/**
 * Link da Apresentação (migration 258, tabela estudo_links): o vendedor grava
 * o estudo já calculado e manda /#/estudo/<id> ao cliente, que abre sem login.
 * O botão "Gerar link" do web (estudoLink.ts) e a North IA
 * (chat-ai/apresentacao.ts) gravam pela mesma regra daqui.
 *
 * O que volta do banco é tratado como dado de fora: qualquer logado pode gravar
 * o JSON que quiser, e a página pública desenha com innerHTML no domínio do
 * app. Por isso sanearJson tira de todo texto os caracteres que abrem tag ou
 * fecham atributo — os modelos de estudoPaginas.ts só usam atributo entre aspas
 * duplas — e estudoValido confere a forma antes de desenhar.
 */
import type { Estudo } from './estudo.ts';

/** Validade do link; a política da tabela aceita até 31 dias. */
export const DIAS_DO_LINK = 30;

/** Quem assina a apresentação (nome, e-mail e telefone do especialista). */
export type Especialista = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

/** Cópia do JSON sem `<`, `>`, `"` e crase nos textos; descarta o que não é JSON. */
export const sanearJson = (v: unknown, profundidade = 0): unknown => {
  if (profundidade > 12) return null;
  if (typeof v === 'string') return v.replace(/[<>"`]/g, '').slice(0, 500);
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'boolean' || v === null) return v;
  if (Array.isArray(v)) return v.slice(0, 2000).map((x) => sanearJson(x, profundidade + 1));
  if (typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, x]) => [k.replace(/[<>"`]/g, ''), sanearJson(x, profundidade + 1)]),
    );
  }
  return null;
};

const numero = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
const lista = (v: unknown) => Array.isArray(v) && v.every(numero);

/** Confere a forma mínima que estudoPaginas.ts lê sem checar. */
export const estudoValido = (v: unknown): v is Estudo => {
  const e = v as Estudo | null;
  if (!e || typeof e !== 'object') return false;
  if (e.segmento !== 'imovel' && e.segmento !== 'veiculo') return false;
  const en = e.entrada as unknown as Record<string, unknown> | undefined;
  if (!en || !['credito', 'prazo', 'taxaAdminPct', 'mesContemplacao'].every((k) => numero(en[k]))) return false;
  if (!e.comparativo || typeof e.comparativo !== 'object' || !e.opcoes || typeof e.opcoes !== 'object') return false;
  if (!e.custo || !lista(e.custo.consorcio) || !lista(e.custo.sac) || !lista(e.custo.price)) return false;
  if (!e.venda || !lista(e.venda.pago) || !lista(e.venda.carta) || !lista(e.venda.cdb)) return false;
  return true;
};

export const especialistaDe = (v: unknown): Especialista => {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const texto = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  return { name: texto(o.name), email: texto(o.email), phone: texto(o.phone) };
};

/** O app usa rota por hash (react-admin): o endereço público é /#/estudo/<id>. */
export const urlDoEstudo = (id: string, origin: string) => `${origin}/#/estudo/${id}`;

/** A linha que vai para estudo_links: estudo e especialista já saneados. */
export const linhaDoLink = (estudo: Estudo, especialista: Especialista) => ({
  cliente: estudo.cliente,
  estudo: sanearJson(estudo),
  especialista: sanearJson(especialistaDe(especialista)),
});
